import { useQuery } from '@tanstack/react-query';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { api } from '@/lib/api';
import { formatVnd } from '@/lib/format';
import { ChartCard } from './chart-card';

const CHART_COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(var(--chart-6))',
  'hsl(var(--chart-7))',
  'hsl(var(--chart-8))',
];

interface DepartmentDonutProps {
  dateFrom?: string;
  dateTo?: string;
  all?: boolean;
}

export function DepartmentDonut({ dateFrom, dateTo, all }: DepartmentDonutProps) {
  const { data } = useQuery({
    queryKey: ['dashboard-by-dept', all ? 'all' : (dateFrom ?? null), dateTo ?? null],
    queryFn: () => api.getByDepartment({ date_from: dateFrom, date_to: dateTo, all }),
  });
  const rows = data?.rows ?? [];
  const total = data?.total_vnd ?? 0;

  return (
    <ChartCard
      title="By department"
      description="Spending — khoảng đã chọn"
      contentClassName="px-4 pb-5"
    >
      {rows.length > 0 ? (
        <>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={rows}
                dataKey="total_vnd"
                nameKey="department_code"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={2}
                label={false}
              >
                {rows.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 8,
                  color: 'hsl(var(--popover-foreground))',
                }}
                formatter={(v, n) => [
                  formatVnd(typeof v === 'number' ? v : parseFloat(String(v ?? 0))),
                  String(n),
                ]}
              />
            </PieChart>
          </ResponsiveContainer>

          {/* Center label showing total */}
          <div className="-mt-2 mb-3 text-center">
            <span className="font-mono text-xs text-muted-foreground">
              Total: {formatVnd(total)}
            </span>
          </div>

          <ul className="space-y-1.5 text-xs">
            {rows.map((r, i) => {
              const pct = total > 0 ? Math.round((r.total_vnd / total) * 100) : 0;
              return (
                <li key={r.department_code} className="flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-sm"
                    style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}
                  />
                  <span className="flex-1 truncate text-foreground">{r.department_code}</span>
                  <span className="shrink-0 font-mono tabular-nums text-muted-foreground">
                    {formatVnd(r.total_vnd)}
                  </span>
                  <span className="w-9 shrink-0 text-right tabular-nums text-muted-foreground/70">
                    {pct}%
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <div className="flex h-[240px] items-center justify-center text-sm text-muted-foreground">
          Không có dữ liệu trong khoảng này
        </div>
      )}
    </ChartCard>
  );
}
